import type { Space } from '@modules/settings/types/space';
import { fill, SHEETS_STRINGS } from '@sharedlib/sheets-editor/sheets.strings';

type Json = Record<string, unknown>;
const isObject = (v: unknown): v is Json => typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * §11.5.3's "Formula cells": cells holding a formula, its own (`f`) or a shared one's (`si`), across every sheet.
 * Counted from the snapshot the detail view already loads for its preview.
 */
export function formulaCellCount(snapshot: unknown): number {
  if (!isObject(snapshot) || !isObject(snapshot['sheets'])) return 0;
  let count = 0;
  for (const sheet of Object.values(snapshot['sheets'])) {
    const cellData = isObject(sheet) && isObject(sheet['cellData']) ? sheet['cellData'] : {};
    for (const row of Object.values(cellData)) {
      if (!isObject(row)) continue;
      for (const cell of Object.values(row)) if (isObject(cell) && (cell['f'] || cell['si'])) count += 1;
    }
  }
  return count;
}

/**
 * §11.5.3's "Engine version": the version of the engine that produced the snapshot, which the snapshot carries
 * itself (`appVersion`: the engine writes it when it makes a workbook, and keeps it on every save). Null when the
 * snapshot has none.
 */
export function engineVersion(snapshot: unknown): string | null {
  const version = isObject(snapshot) ? snapshot['appVersion'] : undefined;
  return typeof version === 'string' && version.trim() ? version.trim() : null;
}

const KB = 1024;
const MB = KB * 1024;
const GB = MB * 1024;

/**
 * §11.5.3's Storage, as the pack formats it: "<x.y> KB" below 1 MB, "<x.y> MB" below 1 GB, else "<x.y> GB", where
 * 1 KB = 1,024 bytes. `bytes` is the server's sum of the workbook's image assets (`storageBytes`).
 */
export function formatStorage(bytes: number): string {
  const size = (unit: number) => (bytes / unit).toFixed(1);
  if (bytes < MB) return fill('{size} KB', { size: size(KB) });
  if (bytes < GB) return fill('{size} MB', { size: size(MB) });
  return fill('{size} GB', { size: size(GB) });
}

/**
 * The Design Lab's read-only "Owner" on Details: the user who made the workbook (`createdBy`), named as the space
 * lists its participants (Wook, 2026-10-07: "make it userid, as space owner will have issues with teams"). A
 * participant's `id` is the account id the server records as `createdBy`. A workbook made before it was recorded has
 * none: in a space of one participant nobody else can have made it, so that participant; in a team, nothing, not the
 * space's owner. One whose maker is no longer a participant (a member who left) shows nothing too, as does an unknown
 * space.
 */
export function workbookOwnerName(
  space: Pick<Space, 'participants'> | null | undefined,
  createdBy: string | null | undefined,
): string | null {
  const participants = space?.participants ?? [];
  const owner = createdBy ? participants.find(({ id }) => id === createdBy) : participants.length === 1 ? participants[0] : undefined;
  const name = owner?.fullName?.trim() || owner?.userName?.trim();
  return name || null;
}

/** The Design Lab's "Status" on Details: the workbook's lifecycle state (§12), in its words. */
export function workbookStatus(item: { readonly deleted?: boolean | null; readonly archived?: boolean | null }): string {
  if (item.deleted) return SHEETS_STRINGS.Trashed;
  if (item.archived) return SHEETS_STRINGS.Archived;
  return SHEETS_STRINGS.Active;
}
